// ./src/components/features/Vouchers/VoucherEditDrawer/VoucherEditDrawer.tsx
//
// Create/edit drawer for a voucher (account-level reusable coupon type — e.g.
// "Free Drink"). Self-loading in edit mode via recordId; context-or-props
// resolution; no success toast (the page highlight is the confirmation). Fields:
// name, description, icon + color, max redemptions, valid window, and the line
// item menu (VoucherItemsEditor), plus status. Items are saved in one shot via
// the nested `items` input (server diffs on item id).

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, NumberInput, Group, Divider, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { CouponType, CouponTypeInput, CouponTypeItem, CouponTypeItemInput } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalCouponTypesContext } from "@/contexts/CouponTypesContext";
import { AppDrawer, StatusInput, IconSelector, ColorPickerInput } from "@/components/generic";

import { VoucherItemsEditor } from "../VoucherItemsEditor/VoucherItemsEditor";

export interface VoucherEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode (e.g. from a "Create X" action). */
  initialName?: string | null;
  onCreated?: (couponType: CouponType) => void;
  onUpdated?: (couponType: CouponType) => void;
}

interface FormData {
  name: string;
  description: string;
  icon: string | null;
  color: string | null;
  maxRedemptions: number;
  couponValue: number | null;
  items: CouponTypeItemInput[];
  status: number;
}

const KNOWN_FORM_FIELDS = [
  "name",
  "description",
  "icon",
  "color",
  "maxRedemptions",
  "couponValue",
  "items",
  "status",
];

const getInitialFormData = (): FormData => ({
  name: "",
  description: "",
  icon: null,
  color: null,
  maxRedemptions: 1,
  couponValue: null,
  items: [],
  status: STATUSES.ACTIVE,
});

const itemFromRecord = (item: CouponTypeItem): CouponTypeItemInput => ({
  id: item.id,
  name: item.name ?? "",
  quantity: item.quantity ?? 1,
  unitCost: item.unitCost ?? null,
  currencyCode: item.currencyCode ?? null,
  isOptional: item.isOptional ?? false,
  inventoryItemId: item.inventoryItemId ?? null,
});

const fromRecord = (record: CouponType): FormData => ({
  name: record.name || "",
  description: record.description || "",
  icon: record.icon ?? null,
  color: record.color ?? null,
  maxRedemptions: record.maxRedemptions ?? 1,
  couponValue: record.couponValue ?? null,
  items: (record.items ?? []).map(itemFromRecord),
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const toMaxRedemptions = (value: number | string): number => {
  const n = Number(value);

  return Number.isFinite(n) && n >= 1 ? Math.round(n) : 1;
};

const toValue = (value: number | string): number | null => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const n = Number(value);

  return Number.isFinite(n) && n >= 0 ? n : null;
};

const VoucherEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const VoucherEditDrawer: React.FC<VoucherEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["vouchers", "common"]);

  const ctx = useOptionalCouponTypesContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<CouponType | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as CouponType);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record)
      : { ...getInitialFormData(), name: initialName?.trim() || "" };
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    // Drop blank-name rows and carry only the writable item fields.
    const items = formData.items
      .filter((item) => (item.name ?? "").trim().length > 0)
      .map((item) => ({
        id: item.id || undefined,
        name: (item.name ?? "").trim(),
        quantity: item.quantity ?? 1,
        unitCost: item.unitCost ?? null,
        currencyCode: item.currencyCode ?? null,
        isOptional: item.isOptional ?? false,
        inventoryItemId: item.inventoryItemId ?? null,
      }));

    const submitData: CouponTypeInput = {
      name: formData.name.trim() || null,
      description: formData.description.trim() || null,
      icon: formData.icon,
      color: formData.color,
      maxRedemptions: formData.maxRedemptions,
      couponValue: formData.couponValue,
      items,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as CouponType[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("vouchers:form.editTitle", { defaultValue: "Edit voucher" })
    : t("vouchers:form.addTitle", { defaultValue: "Add voucher" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <VoucherEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("vouchers:form.fields.name.label", { defaultValue: "Name" })}
            description={t("vouchers:form.fields.name.description", {
              defaultValue: "What this voucher is called, e.g. \"Free Drink\".",
            })}
            placeholder={t("vouchers:form.fields.name.placeholder", { defaultValue: "e.g. Free Drink" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <Textarea
            label={t("vouchers:form.fields.description.label", { defaultValue: "Description" })}
            placeholder={t("vouchers:form.fields.description.placeholder", {
              defaultValue: "Optional — a short note about what this voucher is for.",
            })}
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={4}
            size="md"
          />

          <Group grow align="flex-start">
            <IconSelector
              label={t("vouchers:form.fields.icon.label", { defaultValue: "Icon" })}
              value={formData.icon}
              onChange={(icon) => setField("icon", icon)}
              error={fieldErrors.icon}
              size="md"
            />
            <ColorPickerInput
              label={t("vouchers:form.fields.color.label", { defaultValue: "Color" })}
              value={formData.color}
              onChange={(_, color) => setField("color", color)}
              error={fieldErrors.color}
            />
          </Group>

          <NumberInput
            label={t("vouchers:form.fields.maxRedemptions.label", { defaultValue: "Uses per voucher" })}
            description={t("vouchers:form.fields.maxRedemptions.description", {
              defaultValue: "How many times one issued voucher may be redeemed.",
            })}
            value={formData.maxRedemptions}
            onChange={(v) => setField("maxRedemptions", toMaxRedemptions(v))}
            error={fieldErrors.maxRedemptions}
            min={1}
            allowNegative={false}
            allowDecimal={false}
            size="md"
          />

          <Divider
            label={t("vouchers:form.sections.items", { defaultValue: "What it gives" })}
            labelPosition="left"
          />

          <VoucherItemsEditor
            value={formData.items}
            onChange={(items) => setField("items", items)}
            disabled={isSubmitting}
            error={fieldErrors.items}
          />

          <NumberInput
            label={t("vouchers:form.fields.couponValue.label", { defaultValue: "Value per redemption" })}
            description={t("vouchers:form.fields.couponValue.description", {
              defaultValue:
                "What you owe the vendor each time this voucher is redeemed. Used for reconciliation when the items above have no cost — leave empty if the item costs cover it.",
            })}
            placeholder={t("vouchers:form.fields.couponValue.placeholder", { defaultValue: "e.g. 5.00" })}
            value={formData.couponValue ?? ""}
            onChange={(v) => setField("couponValue", toValue(v))}
            error={fieldErrors.couponValue}
            min={0}
            allowNegative={false}
            decimalScale={2}
            size="md"
          />

          {/* ⚠⚠ THE "VALID WINDOW" FIELDS WERE REMOVED HERE (A4b, owner ruling 2026-09-24).
              `coupon_type.valid_from` / `valid_to` shipped in migration 500 and were ENFORCED NOWHERE —
              `CouponCore.redeem` never looked, and one live voucher type already carried a `valid_to`
              that did nothing — under a hint promising "leave both empty and the voucher is valid any
              time". The expiry is now a per-EVENT setting (the event's voucher rules drawer), which IS
              enforced at redemption. It lives there because a voucher type here is account-wide and may
              be used by several events: an expiry on the TYPE would expire it everywhere it is used.
              ⚠ The COLUMNS still exist — nothing writes them now. Do not re-add these inputs without
              making `redeem` honour them; a control promising a rule nobody enforces is how this got
              here in the first place. */}
<Divider />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
